import React, { useState, useEffect, useMemo } from 'react';
import {
    Table, Header, SpaceBetween, Button, Badge, Box,
    PropertyFilter, Select, SelectProps, PropertyFilterProps, Pagination, CollectionPreferences,
} from '@amzn/awsui-components-react';
import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { getString, getPropertyFilterString } from 'common/ui-string-labels/ui-strings-utils';
import { buildColumnDefinitions, defaultPagingOptions, buildVisibleContentOptions } from 'common/utils/table-utils';
import { ColumnSetting } from 'common/utils/config-models';
import { useEditableRow } from 'common/utils/editable-row';
import { usePreferences } from 'hooks/usePreferences';
import tableConfig from './product-inventory.table-config.json';
import { ProductInventoryItem, NEED_TO_ORDER_OPTIONS, VENDOR_OPTIONS, FILTERING_PROPERTIES } from './interfaces';
import { ProductInventoryService } from 'services/product-inventory-service';
import { calculateRestockThreshold } from 'common/utils/inventory-calculations';
import { convertTokensToPropertyFilter } from 'common/utils/property-filter-helper';
import { describeError } from 'common/utils/error-utils';

export interface ProductInventoryTableProps {
    useFlashBarItemsActions: useFlashBarItemsActions;
}

const PREFERENCES_KEY = 'product-inventory-preferences';
const DEFAULT_PAGE_SIZE = 10;
const ACTIONS_COLUMN_MIN_WIDTH = 180;
const VISIBLE_CONTENT_OPTIONS = buildVisibleContentOptions(tableConfig.columns as ColumnSetting[]);

const ProductInventoryTable: React.FC<ProductInventoryTableProps> = ({ useFlashBarItemsActions }) => {
    const [items, setItems] = useState<ProductInventoryItem[]>([]);
    const [totalResults, setTotalResults] = useState(0);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter state
    const [query, setQuery] = useState<PropertyFilterProps.Query>({ tokens: [], operation: 'and' });
    const [needToOrderFilter, setNeedToOrderFilter] = useState<SelectProps.Option>(NEED_TO_ORDER_OPTIONS[0] as SelectProps.Option);
    const [vendorFilter, setVendorFilter] = useState<SelectProps.Option>(VENDOR_OPTIONS[0] as SelectProps.Option);

    const { editingId, editValues, editConfig, actions: editActions } = useEditableRow();
    const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
        preferencesKey: PREFERENCES_KEY,
        defaultPaging: DEFAULT_PAGE_SIZE,
        columnsSettings: tableConfig.columns as ColumnSetting[],
        refreshData: () => {},
    });

    // Derived search params — memoized so useEffect only fires when values actually change
    const searchParams = useMemo(() => {
        const keyed = convertTokensToPropertyFilter(query.tokens);
        const filters: { [key: string]: string[] } = {};
        if (keyed.product) filters.productName = keyed.product;
        if (keyed.sku) filters.productSku = keyed.sku;
        // Vendor filter is live — backend supports server-side filtering by vendor.
        if (vendorFilter.value && vendorFilter.value !== 'all') filters.vendor = [vendorFilter.value];
        if (needToOrderFilter.value === 'yes') filters.needToReorder = ['true'];
        return Object.keys(filters).length > 0 ? { filters } : {};
    }, [query, vendorFilter.value, needToOrderFilter.value]);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchParams]);

    // Fetch data when searchParams, page, or pageSize change
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        ProductInventoryService.instance.getInventory({
            ...searchParams,
            pageSize: currentPreferences.pageSize!,
            pageIndex: currentPage,
        }).then(data => {
            if (!cancelled) {
                setItems(data.items);
                setTotalResults(data.totalResults);
                setLoading(false);
            }
        });
        return () => { cancelled = true; };
    }, [searchParams, currentPage, currentPreferences.pageSize]);

    // Fetch total needToReorder count from backend (separate call, counts across all pages).
    // Re-fetches on mount and after lead-time updates (which change restock thresholds).
    const [needToOrderCount, setNeedToOrderCount] = useState(0);
    const [reorderCountRefresh, setReorderCountRefresh] = useState(0);
    useEffect(() => {
        let cancelled = false;
        ProductInventoryService.instance.getInventory({ pageSize: 1, pageIndex: 1, filters: { needToReorder: ['true'] } })
            .then(result => { if (!cancelled) setNeedToOrderCount(result.totalResults); })
            .catch(() => { if (!cancelled) setNeedToOrderCount(0); });
        return () => { cancelled = true; };
    }, [reorderCountRefresh]);

    const updateItemLeadTime = (itemId: string, leadTimeDays: number) => {
        setItems(prev => prev.map(i => {
            if (i.productTaxonomyId !== itemId) return i;
            const lastMonthlyOrders = i.details?.lastMonthlyOrders;
            return {
                ...i,
                leadTimeDays,
                restockThreshold: lastMonthlyOrders != null
                    ? calculateRestockThreshold(lastMonthlyOrders, leadTimeDays)
                    : undefined,
            };
        }));
    };

    const saveEdit = async () => {
        if (!editingId) return;
        const leadTimeDays = Number(editValues.leadTimeDays);
        if (isNaN(leadTimeDays) || leadTimeDays < 0) return;

        try {
            await ProductInventoryService.instance.updateLeadTime([editingId], leadTimeDays);
            updateItemLeadTime(editingId, leadTimeDays);
            setReorderCountRefresh(c => c + 1);
            useFlashBarItemsActions.addSuccessFlashBarItem(getString('productInventory.actions.saveSuccess'));
        } catch (error) {
            useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'bulkOrders.flashbar.error'));
        }
        editActions.cancel();
    };

    const baseColumns = buildColumnDefinitions(tableConfig.columns as ColumnSetting[], currentPreferences.visibleContent, { ...editConfig, idField: 'productTaxonomyId' });

    const actionsColumn = {
        id: 'actions', header: getString('productInventory.columns.actions'), minWidth: ACTIONS_COLUMN_MIN_WIDTH,
        cell: (i: ProductInventoryItem) => (
            <SpaceBetween direction="horizontal" size="xs">
                {editingId === i.productTaxonomyId ? (
                    <>
                        <Button data-testid="product-inventory-row-save" variant="primary" onClick={saveEdit}>{getString('productInventory.actions.save')}</Button>
                        <Button data-testid="product-inventory-row-cancel" onClick={editActions.cancel}>{getString('productInventory.actions.cancel')}</Button>
                    </>
                ) : (
                    <Button data-testid="product-inventory-row-edit" onClick={() => editActions.startEdit(i.productTaxonomyId, i)} disabled={editingId !== null}>{getString('productInventory.actions.edit')}</Button>
                )}
            </SpaceBetween>
        ),
    };

    const columns = [...baseColumns, actionsColumn];

    return (
        <Table
            data-testid="product-inventory-table"
            header={
                <Header
                    variant="h1"
                    counter={`(${totalResults})`}
                    info={needToOrderCount > 0 ? <Badge color="red">{needToOrderCount} {getString('productInventory.needReorder')}</Badge> : undefined}
                >
                    {getString('productInventory.title')}
                </Header>
            }
            items={items}
            columnDefinitions={columns}
            loading={loading}
            stripedRows={currentPreferences.stripedRows}
            pagination={<Pagination currentPageIndex={currentPage} pagesCount={Math.ceil(totalResults / currentPreferences.pageSize!)} onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)} />}
            preferences={<CollectionPreferences title={getString('common.preferences.title')} confirmLabel={getString('common.preferences.confirmLabel')} cancelLabel={getString('common.preferences.cancelLabel')} preferences={currentPreferences} onConfirm={onPreferencesConfirmHandler} pageSizePreference={{ title: getString('common.preferences.pageSize.title'), options: defaultPagingOptions }} stripedRowsPreference={{ label: getString('common.preferences.stripedRows.label'), description: getString('common.preferences.stripedRows.description') }} visibleContentPreference={{ title: getString('common.preferences.visibleContentPreference.title'), options: [{ label: getString('common.preferences.visibleContentPreference.columns'), options: VISIBLE_CONTENT_OPTIONS }] }} />}
            filter={
                <SpaceBetween direction="horizontal" size="s">
                    <div data-testid="product-inventory-property-filter" style={{ width: '500px' }}>
                        <PropertyFilter query={query} onChange={({ detail }) => setQuery(detail)} filteringProperties={FILTERING_PROPERTIES} i18nStrings={getPropertyFilterString()} filteringPlaceholder={getString('productInventory.filter.placeholder')} expandToViewport />
                    </div>
                    <Select data-testid="product-inventory-vendor-filter" selectedOption={vendorFilter} onChange={({ detail }) => setVendorFilter(detail.selectedOption)} options={VENDOR_OPTIONS} ariaLabel={getString('productInventory.filter.vendorAriaLabel')} selectedAriaLabel="Selected" />
                    <Select data-testid="product-inventory-need-to-order-filter" selectedOption={needToOrderFilter} onChange={({ detail }) => setNeedToOrderFilter(detail.selectedOption)} options={NEED_TO_ORDER_OPTIONS} ariaLabel={getString('productInventory.filter.needToOrderAriaLabel')} selectedAriaLabel="Selected" />
                </SpaceBetween>
            }
            empty={<Box textAlign="center"><b>{getString('productInventory.table.emptyTitle')}</b></Box>}
            stickyHeader
            variant="full-page"
            resizableColumns
            wrapLines
        />
    );
};

export default ProductInventoryTable;
